/*
 * iter v33 r35 · User: «fai in modo che io non possa entrare nell'annuncio
 * finché non è tutto finito». Root fix per la percezione "non funziona":
 * durante Migliora tutte, un overlay bloccante copre l'annuncio e mostra
 * in tempo reale done/total + le foto in lavorazione in questo momento.
 *
 * Comportamento:
 *  - Backdrop scuro con blur, non chiudibile con ESC / click esterno
 *  - Progress numerico grande + barra + conteggio "in lavorazione ora"
 *  - Bottone "Annulla" (chiama onCancel; il worker si ferma in 3-5s)
 *  - Bottone "Torna alla lista" per uscire dall'annuncio ma mantenere il
 *    job in background (arriverà la push a fine job)
 *
 * Il polling e la logica di aggiornamento sono gestiti da PropertyDetail;
 * qui riceviamo solo lo stato via props.
 */
import { Sparkle, Spinner, ArrowLeft, X } from "@phosphor-icons/react";

export default function BatchProgressOverlay({
  progress,               // { done, total, errors, in_progress }
  onCancel,               // handler pulsante "Annulla"
  onLeaveInBackground,    // handler pulsante "Torna alla lista"
  cancelling = false,     // stato bottone annulla
  propertyTitle = "questo annuncio",
}) {
  if (!progress) return null;
  const done = Number(progress.done || 0);
  const total = Math.max(Number(progress.total || 0), 1);
  const errors = Number(progress.errors || 0);
  const inProgress = Number(progress.in_progress || 0);
  const pct = Math.min(100, Math.round((done / total) * 100));

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-md p-4"
      data-testid="batch-progress-overlay"
      role="dialog"
      aria-modal="true"
      // Blocco propagation cosi' i click sotto non passano.
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-[#1C1C1A] text-white px-6 py-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#A8B89A] font-bold mb-1.5">
            Sto migliorando le foto
          </div>
          <div className="text-[16px] font-semibold leading-snug">
            {propertyTitle}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-5">
          {/* Progress numerico grande */}
          <div className="text-center">
            <div
              className="text-[56px] font-bold text-[#1C1C1A] leading-none tabular-nums"
              data-testid="batch-overlay-progress"
            >
              {done}<span className="text-[#9A968B] text-[36px]"> / {total}</span>
            </div>
            <div className="text-[12px] text-[#6B6A65] mt-1.5">
              {inProgress > 0 ? (
                <span className="inline-flex items-center gap-1.5">
                  <Spinner size={12} className="animate-spin text-[#7C8B6E]" />
                  <span data-testid="batch-in-progress-count">
                    {inProgress} in lavorazione in questo momento
                  </span>
                </span>
              ) : done === total ? (
                "Ci siamo · sto salvando gli ultimi dettagli"
              ) : (
                "Preparando le foto…"
              )}
            </div>
          </div>

          {/* Barra progresso */}
          <div className="w-full h-2 bg-[#EDEAE2] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#7C8B6E] to-[#5C6B4E] transition-all duration-500 ease-out"
              style={{ width: `${pct}%` }}
              data-testid="batch-overlay-bar"
            />
          </div>

          {errors > 0 && (
            <div className="text-[11.5px] text-[#B26A2E] bg-[#FBF3E8] rounded-md px-3 py-2 text-center">
              {errors} {errors === 1 ? "foto non riuscita" : "foto non riuscite"} · le potrai riprovare a mano a fine batch
            </div>
          )}

          {/* Info: puoi lasciare l'app aperta o chiuderla */}
          <div className="text-[11.5px] text-[#6B6A65] leading-relaxed bg-[#F1F4ED] rounded-md px-3 py-2.5 text-center">
            <Sparkle size={12} weight="fill" className="inline text-[#7C8B6E] mr-1" />
            Puoi anche chiudere l&apos;app · ti arriva una notifica quando ho finito.
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2 pt-1">
            <button
              type="button"
              onClick={onLeaveInBackground}
              className="w-full py-3 rounded-md bg-white border border-[#E5E7E0] hover:border-[#7C8B6E] text-[#1C1C1A] font-medium text-[13px] transition-colors inline-flex items-center justify-center gap-2"
              data-testid="batch-overlay-leave"
            >
              <ArrowLeft size={13} weight="bold" /> Torna alla lista · continua in background
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={cancelling}
              className="w-full py-2.5 rounded-md text-[#D14D4D] hover:bg-[#FFEEEE] disabled:opacity-60 font-medium text-[12.5px] transition-colors inline-flex items-center justify-center gap-1.5"
              data-testid="batch-overlay-cancel"
            >
              {cancelling ? (
                <><Spinner size={11} className="animate-spin" /> Sto annullando…</>
              ) : (
                <><X size={11} weight="bold" /> Annulla Migliora tutte</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
